import React, { useEffect, useState } from 'react'
import {
  CalendarDays,
  Camera,
  Check,
  ChevronDown,
  ChevronRight,
  Inbox,
  Scissors,
  X,
} from 'lucide-react'
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '@/components/ui/sheet'
import { toast } from '@/components/ui/sonner'
import { cn } from '@/lib/utils'
import {
  useGetPostSuggestionsQuery,
  useRespondToSuggestionsMutation,
  type PlannedPost,
} from '@/redux/collaborationsApi'
import { fmt, localToday } from './postHelpers'

const platformLabel = (p: string) =>
  p === 'tiktok' ? 'TikTok' : p === 'youtube' ? 'YouTube' : 'Instagram'

const SuggestionCard: React.FC<{
  p: PlannedPost
  busy: boolean
  onAnswer: (accept: boolean) => void
}> = ({ p, busy, onAnswer }) => {
  const [open, setOpen] = useState(false)
  const dated = !!p.plannedOn && p.plannedOn >= localToday()
  return (
    <div className="rounded-2xl border border-border bg-card p-3 shadow-sm">
      <p className="text-xs font-semibold text-[#3F7A0A]">
        {p.theme?.label ?? 'Post'} · {platformLabel(p.platform)}
      </p>
      <p className="font-bold leading-snug text-foreground">{p.idea ?? 'Post idea'}</p>
      {p.hook ? <p className="mt-1.5 text-sm italic text-foreground">“{p.hook}”</p> : null}
      <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
        {dated ? (
          <>
            {p.shootOn ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#CCFBF1] px-2 py-0.5 font-semibold text-[#0F766E]">
                <Camera className="h-3 w-3" /> Shoot{' '}
                {fmt(p.shootOn, { weekday: 'short', month: 'short', day: 'numeric' })}
              </span>
            ) : null}
            {p.editOn ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-[#DBEAFE] px-2 py-0.5 font-semibold text-[#1D4ED8]">
                <Scissors className="h-3 w-3" /> Edit {fmt(p.editOn, { weekday: 'short' })}
              </span>
            ) : null}
            <span className="inline-flex items-center gap-1 rounded-full bg-[#FCE7F3] px-2 py-0.5 font-semibold text-[#BE185D]">
              <CalendarDays className="h-3 w-3" /> Post{' '}
              {fmt(p.plannedOn!, { weekday: 'short', month: 'short', day: 'numeric' })}
              {p.plannedTime ? ` · ${p.plannedTime}` : ''}
            </span>
          </>
        ) : (
          <span className="rounded-full bg-muted px-2 py-0.5 font-semibold text-muted-foreground">
            Goes to your idea bank (pick a day later)
          </span>
        )}
      </div>
      {p.concept || p.shotList.length || p.location || p.wardrobe || p.caption ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-muted-foreground"
            aria-expanded={open}
          >
            {open ? 'Hide the plan' : 'See the plan'}
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform', open && 'rotate-180')}
            />
          </button>
          {open ? (
            <div className="mt-2 space-y-2 text-sm">
              {p.concept ? <p className="text-foreground">{p.concept}</p> : null}
              {p.whyNow ? <p className="text-muted-foreground">Why now: {p.whyNow}</p> : null}
              {p.shotList.length ? (
                <ol className="list-decimal space-y-0.5 pl-5 text-foreground">
                  {p.shotList.map((s, i) => (
                    <li key={i}>{s.text}</li>
                  ))}
                </ol>
              ) : null}
              {p.location || p.wardrobe ? (
                <p className="text-muted-foreground">
                  {[p.location && `Where: ${p.location}`, p.wardrobe && `Wear: ${p.wardrobe}`]
                    .filter(Boolean)
                    .join(' · ')}
                </p>
              ) : null}
              {p.caption ? (
                <p className="rounded-lg bg-muted px-2.5 py-1.5 text-foreground">{p.caption}</p>
              ) : null}
            </div>
          ) : null}
        </>
      ) : null}
      <div className="mt-3 grid grid-cols-[1fr_auto] gap-2">
        <button
          type="button"
          disabled={busy}
          onClick={() => onAnswer(true)}
          className="inline-flex h-10 items-center justify-center gap-1.5 rounded-xl bg-[#C0FD72] text-sm font-semibold text-[#0F172A] hover:bg-[#B2F25E] disabled:opacity-50"
        >
          <Check className="h-4 w-4" strokeWidth={3} /> Add to my calendar
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onAnswer(false)}
          className="inline-flex h-10 items-center justify-center gap-1 rounded-xl border border-border bg-card px-4 text-sm font-semibold text-foreground disabled:opacity-50"
        >
          <X className="h-4 w-4" /> Skip
        </button>
      </div>
    </div>
  )
}

/**
 * Posts a manager suggested (planned with the content assistant): a banner on the calendar
 * and a sheet to review them. Each one can be added to the calendar or skipped; nothing
 * counts toward the week until it's added.
 */
export const SuggestedPosts: React.FC<{
  /** Open the review straight away (from the notification link) */
  openOnLoad?: boolean
  onChanged?: () => void
}> = ({ openOnLoad = false, onChanged }) => {
  const { data } = useGetPostSuggestionsQuery()
  const [respond, { isLoading: busy }] = useRespondToSuggestionsMutation()
  const [open, setOpen] = useState(false)
  const suggestions = data?.suggestions ?? []
  const from = suggestions[0]?.plannedBy.name ?? 'Your manager'
  const fromFirst = from.split(' ')[0]
  const note = suggestions.find((p) => p.suggestionNote)?.suggestionNote ?? null

  useEffect(() => {
    if (openOnLoad && suggestions.length) setOpen(true)
  }, [openOnLoad, suggestions.length])
  useEffect(() => {
    if (open && data && !suggestions.length) setOpen(false)
  }, [open, data, suggestions.length])

  const answer = async (accept: string[], skip: string[]) => {
    try {
      const res = await respond({ accept, skip }).unwrap()
      toast(
        res.added
          ? `${res.added} post${res.added === 1 ? '' : 's'} added to your calendar`
          : 'Skipped',
        res.added ? { description: 'Shoot and edit days are on it too.' } : undefined,
      )
      onChanged?.()
    } catch (err: any) {
      toast.error('That didn’t save', { description: err?.data?.error ?? err?.message })
    }
  }

  if (!suggestions.length) return null
  const n = suggestions.length

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex w-full items-center gap-3 rounded-2xl border border-[#F5C26B] bg-[#FFFBEB] px-4 py-3.5 text-left shadow-sm"
      >
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#FEF3C7] text-[#B45309]">
          <Inbox className="h-5 w-5" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block font-bold text-foreground">
            {fromFirst} suggested {n} post{n === 1 ? '' : 's'}
          </span>
          <span className="block truncate text-sm text-muted-foreground">
            {note ?? 'Review them and add the ones you like'}
          </span>
        </span>
        <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
      </button>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="bottom"
          className="mx-auto flex h-[92dvh] max-w-lg flex-col rounded-t-3xl p-0"
        >
          <div className="border-b border-border px-4 pb-3 pt-4">
            <SheetTitle className="text-lg font-bold text-foreground">
              Suggested by {from}
            </SheetTitle>
            <SheetDescription className="text-sm text-muted-foreground">
              Add the ones you like to your calendar. Skip the rest; {fromFirst} sees what you
              picked.
            </SheetDescription>
            {note ? (
              <p className="mt-2 rounded-xl bg-[#FFFBEB] px-3 py-2 text-sm text-foreground">
                “{note}”
              </p>
            ) : null}
          </div>
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3">
            {suggestions.map((p) => (
              <SuggestionCard
                key={p.id}
                p={p}
                busy={busy}
                onAnswer={(ok) => answer(ok ? [p.id] : [], ok ? [] : [p.id])}
              />
            ))}
          </div>
          {n > 1 ? (
            <div className="border-t border-border px-4 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-3">
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  answer(
                    suggestions.map((p) => p.id),
                    [],
                  )
                }
                className="h-11 w-full rounded-xl bg-[#0F172A] text-sm font-semibold text-[#FFFFFF] disabled:opacity-50"
              >
                Add all {n} to my calendar
              </button>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </>
  )
}

export default SuggestedPosts
